import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import MojitoModules from 'mojito/modules';
import MojitoServices from 'mojito/services';
import { keyBy, groupBy, uniq } from 'mojito/utils';
import { useMarketItems, useEventsChunkRequest, useContentItems } from 'modules/common/hooks';

import { STAT_DEFINITIONS, VIEW } from './types.js';

const { selectEvents, makeSelectEventsState, makeSelectMarketsState } =
    MojitoServices.SportsContent.Events.selectors;
const { isContentLoadDone } = MojitoServices.Common.utils;
const { EventTableUtils, EventBracketUtils } = MojitoModules;

const EMPTY_ARRAY = [];

function applyLinkedNamesToGroups(eventTableGroups, linkedNamesByParticipantId) {
    return Object.fromEntries(
        Object.entries(eventTableGroups).map(([groupKey, group]) => [
            groupKey,
            {
                ...group,
                data: (group?.data || []).map(row => {
                    const linkedName = linkedNamesByParticipantId[String(row.rowId)];
                    return linkedName ? { ...row, countryName: linkedName } : row;
                }),
            },
        ])
    );
}

function linkGroupMarketParticipantNames(eventTableGroups, tournament, markets) {
    const adaptedMarkets = EventTableUtils.adaptStoreMarketsToEventTableMarkets(markets || []);
    const marketsById = keyBy(adaptedMarkets, market => String(market.mktId ?? market.id));
    const linkedNamesByParticipantId = {};

    (tournament?.stages || []).forEach(stage => {
        (stage?.standings || []).forEach(row => {
            for (const selection of row?.selections || []) {
                const market = marketsById[String(selection?.marketId)];
                const linkedSelection = market?.selectionsById?.[String(selection?.selectionId)];
                const linkedName = EventTableUtils.resolveFeedText(linkedSelection?.name);

                if (linkedName) {
                    linkedNamesByParticipantId[String(row.id)] = linkedName;
                    break;
                }
            }
        });
    });

    return applyLinkedNamesToGroups(eventTableGroups, linkedNamesByParticipantId);
}

function buildKnockoutLinkedNames(knockoutStage, eventsById, language) {
    const linkedNamesByParticipantId = {};

    (knockoutStage?.rounds || []).forEach(round => {
        (round?.matches || []).forEach(match => {
            const eventId = EventBracketUtils.getMatchEventId(match);
            const eventParticipants = eventsById[eventId]?.participants || [];
            const byPosition = position =>
                eventParticipants.find(
                    participant => String(participant?.position).toUpperCase() === position
                );
            const homeParticipant = byPosition('HOME') || eventParticipants[0];
            const awayParticipant = byPosition('AWAY') || eventParticipants[1];

            [
                [match?.slot1, homeParticipant],
                [match?.slot2, awayParticipant],
            ].forEach(([slot, linkedParticipant]) => {
                const participantId = slot?.participantId;
                const linkedName = EventTableUtils.resolveFeedText(
                    linkedParticipant?.name,
                    language
                );
                if (participantId && linkedName) {
                    linkedNamesByParticipantId[String(participantId)] = linkedName;
                }
            });
        });
    });

    return linkedNamesByParticipantId;
}

export function useTournamentTitle(tournament, leagueType) {
    return useMemo(() => {
        const fromTournament = EventTableUtils.resolveFeedText(tournament?.name);
        if (fromTournament) {
            return fromTournament;
        }
        return leagueType?.name || '';
    }, [tournament?.name, leagueType?.name]);
}

export function useGroupStandingsTable(tournament, stringResolver) {
    const marketIds = useMemo(
        () =>
            tournament
                ? EventTableUtils.getTournamentMarketIds(tournament).map(String)
                : EMPTY_ARRAY,
        [tournament]
    );
    const linkedMarkets = useMarketItems(marketIds);
    const linkedEventIds = useMemo(
        () =>
            uniq(
                linkedMarkets
                    .map(market => market.eventNodeId ?? market.eventId)
                    .filter(eventId => eventId !== null && eventId !== undefined)
                    .map(String)
            ),
        [linkedMarkets]
    );

    useEventsChunkRequest(linkedEventIds);

    const linkedEvents = useContentItems(selectEvents, linkedEventIds);
    const storeMarkets = useMemo(
        () => EventTableUtils.filterOutrightMarkets(linkedMarkets, linkedEvents),
        [linkedMarkets, linkedEvents]
    );
    const marketsPayload = useMemo(() => ({ markets: storeMarkets || [] }), [storeMarkets]);

    const eventTableGroups = useMemo(() => {
        const groups = EventTableUtils.getEventTableDataAndColumnsFromLeagues(
            { tournaments: tournament ? [tournament] : [] },
            marketsPayload,
            { statDefinitions: STAT_DEFINITIONS, stringResolver }
        );
        return linkGroupMarketParticipantNames(groups, tournament, storeMarkets);
    }, [tournament, marketsPayload, storeMarkets, stringResolver]);
    const groupEntries = useMemo(() => Object.entries(eventTableGroups), [eventTableGroups]);

    const tableColumns = useMemo(
        () => Object.values(eventTableGroups).map(group => group?.columns || []),
        [eventTableGroups]
    );

    const availableMarketGroups = useMemo(
        () => EventTableUtils.adaptSharedOddsMarketsForSelector(tableColumns),
        [tableColumns]
    );

    const defaultSelectedMarketGroupId = useMemo(
        () => EventTableUtils.getDefaultSelectedMarketGroupId(availableMarketGroups),
        [availableMarketGroups]
    );

    const showStatsToggle = useMemo(
        () => tableColumns.some(columns => EventTableUtils.shouldShowStatsToggle(columns)),
        [tableColumns]
    );

    const [selectedMarketGroupId, setSelectedMarketGroupId] = useState();

    useEffect(() => {
        if (!availableMarketGroups.length) {
            setSelectedMarketGroupId(undefined);
            return;
        }

        setSelectedMarketGroupId(currentId => {
            if (currentId && availableMarketGroups.some(group => group.id === currentId)) {
                return currentId;
            }
            return defaultSelectedMarketGroupId;
        });
    }, [availableMarketGroups, defaultSelectedMarketGroupId]);

    return {
        groupEntries,
        availableMarketGroups,
        selectedMarketGroupId,
        setSelectedMarketGroupId,
        showStatsToggle,
        storeMarkets,
    };
}

export function useKnockoutBracket(tournament, storeMarkets, language, marketTypes) {
    const knockoutStage = useMemo(
        () => (tournament ? EventBracketUtils.selectKnockoutStage(tournament) : undefined),
        [tournament]
    );
    const bracketEventIds = useMemo(
        () => EventBracketUtils.collectStageEventIds(knockoutStage),
        [knockoutStage]
    );
    const bracketLinkedMarketIds = useMemo(
        () => EventBracketUtils.collectStageMarketIds(knockoutStage),
        [knockoutStage]
    );

    useEventsChunkRequest(bracketEventIds);

    const bracketEvents = useContentItems(selectEvents, bracketEventIds);
    const bracketMarketIds = useMemo(
        () =>
            uniq([
                ...(bracketEvents || []).flatMap(event =>
                    EventBracketUtils.collectEventMarketIds(event)
                ),
                ...bracketLinkedMarketIds,
            ]),
        [bracketEvents, bracketLinkedMarketIds]
    );
    const bracketMarkets = useMarketItems(bracketMarketIds);
    const bracketMarketsById = useMemo(
        () =>
            keyBy((bracketMarkets || []).filter(Boolean), market =>
                String(market.mktId ?? market.id)
            ),
        [bracketMarkets]
    );
    const eventsById = useMemo(
        () => keyBy((bracketEvents || []).filter(Boolean), event => String(event.id)),
        [bracketEvents]
    );
    const marketsByEventId = useMemo(
        () =>
            groupBy((bracketMarkets || []).filter(Boolean), market =>
                String(market.eventNodeId ?? market.eventId)
            ),
        [bracketMarkets]
    );
    const linkedParticipantNames = useMemo(
        () => buildKnockoutLinkedNames(knockoutStage, eventsById, language),
        [knockoutStage, eventsById, language]
    );
    const bracketRounds = useMemo(
        () =>
            tournament
                ? EventBracketUtils.transformKnockoutData(tournament, {
                      eventsById,
                      marketsById: bracketMarketsById,
                      marketsByEventId,
                      storeMarkets: storeMarkets || [],
                      language,
                      marketTypes,
                  }).rounds
                : [],
        [
            tournament,
            eventsById,
            bracketMarketsById,
            marketsByEventId,
            storeMarkets,
            language,
            marketTypes,
        ]
    );

    const selectBracketEventsState = useMemo(makeSelectEventsState, []);
    const selectBracketMarketsState = useMemo(makeSelectMarketsState, []);
    const bracketEventsState = useSelector(state =>
        selectBracketEventsState(bracketEventIds, state)
    );
    const bracketMarketsState = useSelector(state =>
        selectBracketMarketsState(bracketMarketIds, state)
    );
    const bracketContentLoading = useMemo(() => {
        if (!tournament) {
            return true;
        }

        const eventsLoading =
            bracketEventIds.length > 0 &&
            !bracketEventIds.every(eventId => isContentLoadDone(bracketEventsState[eventId]));
        const marketsLoading =
            bracketMarketIds.length > 0 &&
            !bracketMarketIds.every(marketId => isContentLoadDone(bracketMarketsState[marketId]));

        return eventsLoading || marketsLoading;
    }, [tournament, bracketEventIds, bracketEventsState, bracketMarketIds, bracketMarketsState]);

    const hasKnockoutContent = bracketRounds.some(round => (round?.nodes || []).length > 0);

    return {
        bracketRounds,
        marketsByEventId,
        bracketMarketsById,
        bracketContentLoading,
        hasKnockoutContent,
        linkedParticipantNames,
    };
}

export function useLinkedGroupEntries(groupEntries, linkedParticipantNames) {
    return useMemo(
        () =>
            groupEntries.map(([groupKey, group]) => [
                groupKey,
                applyLinkedNamesToGroups({ [groupKey]: group }, linkedParticipantNames || {})[
                    groupKey
                ],
            ]),
        [groupEntries, linkedParticipantNames]
    );
}

export function useTableViewUiState({ hasKnockoutContent, tournament, emitPerformanceMark }) {
    const [selectedView, setSelectedView] = useState(VIEW.GROUP);
    const [showAllStats, setShowAllStats] = useState(false);

    useEffect(() => {
        if (!hasKnockoutContent && selectedView === VIEW.KNOCKOUT) {
            setSelectedView(VIEW.GROUP);
        }
    }, [hasKnockoutContent, selectedView]);

    useEffect(() => {
        emitPerformanceMark('moduleRendered', Boolean(tournament));
    }, [emitPerformanceMark, tournament]);

    return {
        selectedView,
        setSelectedView,
        showAllStats,
        setShowAllStats,
    };
}
